import type { ReactNode } from 'react';
import { formatDate, formatTime, toBerlinDateKey } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import type { Domain, DomainRecord } from './types';
import styles from './DomainStatusCards.module.css';

/** „heute 14:12" or „16.09.2026, 14:12". */
export function dayTime(value: string): string {
  const today = toBerlinDateKey(new Date()) === toBerlinDateKey(value);
  return today ? `heute ${formatTime(value)}` : `${formatDate(value)}, ${formatTime(value)}`;
}

/** „a", „a und b", „a, b und c". */
export function listAddresses(addresses: readonly string[]): string {
  if (addresses.length <= 1) return addresses.join('');
  return `${addresses.slice(0, -1).join(', ')} und ${addresses[addresses.length - 1] ?? ''}`;
}

export type CheckTone = 'done' | 'warn' | 'open';

export interface DomainCheckRow {
  key: 'domain' | 'cname' | 'txt' | 'tls';
  label: string;
  tone: CheckTone;
}

/**
 * The check rows of Admin 08 / Einstellungen 22: „Domain brimo.de erreichbar", „CNAME portal nicht gefunden",
 * „TXT-Verifizierung offen" (E22 without the names).
 */
export function domainCheckRows(domain: Domain, withNames: boolean): DomainCheckRow[] {
  const zone = withNames && domain.zone ? ` ${domain.zone}` : '';
  const sub = withNames && domain.subdomain ? ` ${domain.subdomain}` : '';
  return [
    domain.checks.domain
      ? { key: 'domain', label: `Domain${zone} erreichbar`, tone: 'done' }
      : { key: 'domain', label: `Domain${zone} nicht erreichbar`, tone: 'warn' },
    domain.checks.cname
      ? { key: 'cname', label: `CNAME${sub} gefunden`, tone: 'done' }
      : { key: 'cname', label: `CNAME${sub} nicht gefunden`, tone: domain.checks.domain ? 'warn' : 'open' },
    domain.checks.txt
      ? { key: 'txt', label: 'TXT-Verifizierung bestätigt', tone: 'done' }
      : { key: 'txt', label: 'TXT-Verifizierung offen', tone: 'open' },
  ];
}

export function CheckRows({ rows, className }: { rows: DomainCheckRow[]; className?: string | undefined }) {
  return (
    <ul className={clsx(styles.checks, className)}>
      {rows.map((row) => (
        <li key={row.key} className={clsx(styles.check, row.tone === 'open' && styles.checkOpen)}>
          <span
            className={clsx(
              styles.dot,
              row.tone === 'done' && styles.dotDone,
              row.tone === 'warn' && styles.dotWarn,
              row.tone === 'open' && styles.dotOpen,
            )}
            aria-hidden="true"
          >
            {row.tone === 'done' ? '✓' : row.tone === 'warn' ? '!' : null}
          </span>
          {row.label}
        </li>
      ))}
    </ul>
  );
}

/** The records table (Typ / Name / Wert / TTL). */
export function RecordsTable({
  records,
  columns,
}: {
  records: readonly Pick<DomainRecord, 'type' | 'name' | 'value' | 'ttl'>[];
  /** E22 uses narrower columns. */
  columns?: string | undefined;
}) {
  const style = columns ? { gridTemplateColumns: columns } : undefined;
  return (
    <div className={styles.table} role="table" aria-label="DNS-Einträge">
      <div className={clsx(styles.row, styles.headRow)} role="row" style={style}>
        <span role="columnheader">Typ</span>
        <span role="columnheader">Name</span>
        <span role="columnheader">Wert</span>
        <span role="columnheader">TTL</span>
      </div>
      {records.map((record) => (
        <div key={`${record.type}-${record.name}`} className={styles.row} role="row" style={style}>
          <span role="cell">{record.type}</span>
          <span role="cell">{record.name}</span>
          <span role="cell">{record.value}</span>
          <span role="cell">{record.ttl}</span>
        </div>
      ))}
    </div>
  );
}

/** Plain text of the records for „Werte kopieren". */
export function recordsText(domain: Domain): string {
  return domain.records
    .map((record) => `${record.type} · ${record.name} · ${record.value} · TTL ${record.ttl}`)
    .join('\n');
}

export interface DomainWaitingCardProps {
  domain: Domain;
  checking: boolean;
  checkError: string | null;
  onCheck: () => void;
  /** The primary of the card („Weiter zur Telefonie"). */
  primary: ReactNode;
}

/** Admin 08 „wartet auf Dritte": navy card with spinner, the sent guide, the check rows and the note. */
export function DomainWaitingCard({
  domain,
  checking,
  checkError,
  onCheck,
  primary,
}: DomainWaitingCardProps) {
  const sent = domain.itMail
    ? `Anleitung ${dayTime(domain.itMail.sentAt)} an ${listAddresses(domain.itMail.recipients)} gesendet. `
    : '';
  const next = domain.nextCheckAt ? `Nächste Prüfung um ${formatTime(domain.nextCheckAt)}.` : '';
  return (
    <section
      className={clsx(styles.card, styles.navy)}
      aria-labelledby="domain-waiting-title"
      aria-busy="true"
    >
      <div className={styles.titleRow}>
        <span className={styles.spinner} aria-hidden="true" />
        <h2 id="domain-waiting-title" className={styles.titleNavy}>
          Wir warten auf den DNS-Eintrag
        </h2>
      </div>
      {sent || next ? (
        <p className={styles.text}>
          {sent}
          {next}
        </p>
      ) : null}
      <CheckRows rows={domainCheckRows(domain, true)} />
      {domain.lastError ? <div className={styles.error}>{domain.lastError}</div> : null}
      {checkError ? (
        <div className={styles.error} role="alert">
          {checkError}
        </div>
      ) : null}
      <div className={styles.note}>
        Sie können das Onboarding fortsetzen. Sobald die Einträge stehen, schalten wir das Portal frei und
        schicken Ihnen eine Mail.
      </div>
      <div className={styles.actions}>
        <button
          type="button"
          className={clsx('pressable', styles.actionNavy)}
          aria-busy={checking || undefined}
          disabled={checking}
          onClick={onCheck}
        >
          Jetzt prüfen
        </button>
        {primary}
      </div>
    </section>
  );
}

export interface DomainVerifiedCardProps {
  domain: Domain;
}

/**
 * Admin 08 „verifiziert": host active, the four facts, „Portal öffnen". The frame's publication warning is gone: the
 * legal texts no longer gate the portal (D-333).
 */
export function DomainVerifiedCard({ domain }: DomainVerifiedCardProps) {
  const host = domain.hostname ?? '';
  const active = domain.state === 'active';
  const tls =
    domain.checks.tls.ok && domain.checks.tls.validUntil
      ? `gültig bis ${formatDate(domain.checks.tls.validUntil)}`
      : 'wird ausgestellt';
  const mail = domain.mail
    ? domain.mail.spfDkim
      ? 'SPF und DKIM gesetzt'
      : 'Wartet auf DNS-Einträge'
    : 'Nicht eingerichtet';
  return (
    <section className={clsx(styles.card, styles.light)} aria-labelledby="domain-verified-title">
      <div className={styles.titleRow}>
        <span className={styles.successIcon} aria-hidden="true">
          ✓
        </span>
        <div className={styles.titleBlock}>
          <h2 id="domain-verified-title" className={styles.title}>
            {active ? `${host} ist aktiv` : `${host} ist verifiziert`}
          </h2>
          {domain.verifiedAt ? (
            <span className={styles.sub}>
              Verifiziert {formatDate(domain.verifiedAt)}, {formatTime(domain.verifiedAt)}
            </span>
          ) : null}
        </div>
      </div>
      <dl className={styles.facts}>
        <div className={styles.fact}>
          <dt className={styles.factLabel}>CNAME</dt>
          <dd className={styles.factValue}>{domain.checks.cname ? 'Gefunden' : 'Nicht gefunden'}</dd>
        </div>
        <div className={styles.fact}>
          <dt className={styles.factLabel}>TXT</dt>
          <dd className={styles.factValue}>{domain.checks.txt ? 'Bestätigt' : 'Offen'}</dd>
        </div>
        <div className={styles.fact}>
          <dt className={styles.factLabel}>TLS-Zertifikat</dt>
          <dd className={styles.factValue}>{tls}</dd>
        </div>
        <div className={styles.fact}>
          <dt className={styles.factLabel}>Mailversand</dt>
          <dd className={styles.factValue}>{mail}</dd>
        </div>
      </dl>
      <div className={styles.actions}>
        {domain.portalUrl && active ? (
          <a
            className={clsx('pressable', styles.action)}
            href={domain.portalUrl}
            target="_blank"
            rel="noreferrer"
          >
            Portal öffnen
          </a>
        ) : null}
      </div>
    </section>
  );
}

export const domainCardStyles = styles;
